// CompanyChip — the single, universal way to display a company across every
// workspace. Given an organization {id, name} it renders a context-preserving
// link to that account under the CURRENT persona prefix (Fleet→/ops/accounts/:id,
// etc.), carrying an origin so the account page can navigate back. With no id it
// degrades to plain text (e.g. an unresolved tenant). Replaces the raw UUIDs and
// dead "company" labels that used to be scattered across Fleet/Ops views.
import React from 'react';
import { Link, useLocation } from 'react-router';
import { Building2 } from 'lucide-react';
import { personaBase } from '@/lib/persona';

export interface CompanyOrigin {
  type: string; // 'fleet' | 'job' | 'vuln' | 'license' | …
  id?: string;
  name?: string;
}

interface CompanyChipProps {
  id?: string | null;
  name?: string | null;
  origin?: CompanyOrigin;
  fallback?: string; // shown when there's no name (default '—')
  icon?: boolean; // show the building glyph (default true)
  weight?: number; // font weight (default 600)
  size?: number; // font size px (default 13)
}

export function CompanyChip({ id, name, origin, fallback = '—', icon = true, weight = 600, size = 13 }: CompanyChipProps) {
  const location = useLocation();
  const base = personaBase(location.pathname);
  const label = name || fallback;

  const inner = (
    <>
      {icon && <Building2 size={Math.round(size * 0.92)} style={{ flexShrink: 0, opacity: 0.7 }} />}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
    </>
  );

  // No id (or no name) → not linkable; render as plain text.
  if (!id || !name) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: size, fontWeight: weight, color: 'var(--text-secondary)', maxWidth: '100%' }}>
        {inner}
      </span>
    );
  }

  return (
    <Link
      to={`${base}/accounts/${id}`}
      state={{ from: origin }}
      onClick={(e) => e.stopPropagation()}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: size, fontWeight: weight, color: 'var(--accent)', textDecoration: 'none', maxWidth: '100%' }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.textDecoration = 'underline'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.textDecoration = 'none'; }}
      title={`Open ${name}`}
    >
      {inner}
    </Link>
  );
}
